import React, { useState } from 'react';
import { AltBadge } from 'views/Main.js';
import Appearance from 'styles/Appearance.js';
import { LayerItem } from 'structure/Layer.js';
import { Map } from 'views/MapElements.js';

export const formatFields = (utils, sections) => {

    // loop through sections and determine next steps
    return sections.filter(section => {

        // skip section if section is not visible
        if(section.visible === false) {
            return false;
        }

        // determine if permissions were providedand if the current user is unable to view this information
        if(section.permissions) {
            let match = section.permissions.find(key => utils.user.permissions.get(key) === false);
            return match ? false : true;
        }
        return true;

    }).map(section => {

        // filter down items to only visible items
        section.items = section.items.filter(item => item.visible !== false);
        return section;

    }).filter(section => {
        return section.items.length > 0;
    });
}

const FieldMapper = ({ sections = [], utils }) => {

    const [shouldShowInteractiveMap, setShouldShowInteractiveMap] = useState(false);
    const [showSensitiveFields, setShowSensitiveFields] = useState(false);

    const onIncompleteAddressClick = () => {
        utils.alert.show({
            title: 'About this Map',
            message: 'The location provided is incomplete or invalid. Please provide a street address, city, state, and zipcode to take advantage of location based services.'
        });
    }

    const getMapComponent = (item, index) => {

        // no additional logic is required if no value was provided
        if(!item.value) {
            return null;
        }

        // return a placeholder map image if location is not valid
        if(item.valid === false) {
            return (
                <div
                key={index}
                className={'text-button'}
                onClick={onIncompleteAddressClick}
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%'
                }}>
                    <img
                    src={window.theme === 'dark' ? 'images/map-placeholder-dark.jpg' : 'images/map-placeholder.jpg'}
                    style={{
                        height: 250,
                        width: '100%'
                    }}/>
                    <img
                    src={'images/red-x-icon.png'}
                    style={{
                        height: 40,
                        position: 'absolute',
                        width: 40
                    }} />
                </div>
            )
        }

        // return a static map image if applicable
        if(item.static && shouldShowInteractiveMap === false) {
            return (
                <div 
                key={index}
                style={{
                    position: 'relative',
                    width: '100%'
                }}>
                    <img
                    src={item.static}
                    style={{
                        height: 250,
                        width: '100%'
                    }}/>
                    <AltBadge 
                    content={{
                        color: Appearance.colors.primary(),
                        text: 'Click to Unlock Map'
                    }} 
                    invert={true}
                    onClick={setShouldShowInteractiveMap.bind(this, true)}
                    style={{
                        bottom: 12,
                        padding: '5px 12px 5px 12px',
                        position: 'absolute',
                        right: 0  
                    }}/>
                </div>
            );
        }

        // fallback to returning a rendered map component
        return (
            <Map
            annotations={[{
                key: 'location',
                location: item.value
            }]}
            center={item.value}
            isRotationEnabled={true}
            isScrollEnabled={true}
            isZoomEnabled={true}
            key={index}
            useShadows={false}
            style={{
                height: 250,
                width: '100%'
            }}/>
        );
    }

    const getValue = item => {
        if(!item.value) {
            return null;
        }
        if(item.isSecure === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1
                }}>
                    {showSensitiveFields === true && (
                        <span style={{
                            ...Appearance.textStyles.value(),
                            flexGrow: 1,
                            ...item.style?.value,
                            ...item.color && {
                                color: item.color
                            }
                        }}>{item.value || 'Not Added'}</span>
                    )}
                    {showSensitiveFields === false && ( 
                        <span style={{
                            ...Appearance.textStyles.value(),
                            flexGrow: 1,
                            ...item.style?.value,
                            ...item.color && {
                                color: item.color
                            }
                        }}>{'•••••••••••••••'}</span>
                    )}
                    <img
                    className={'cursor-pointer'}
                    onClick={setShowSensitiveFields.bind(this, !showSensitiveFields)}
                    src={showSensitiveFields ? `images/visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png` : `images/non-visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png`}
                    style={{
                        height: 25,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 25
                    }} />
                </div>
            )
        }
        return (
            <span style={{
                ...Appearance.textStyles.value(),
                flexGrow: 1,
                ...item.style?.value,
                ...item.color && {
                    color: item.color
                }
            }}>{item.value}</span>
        )
    }

    return formatFields(utils, sections).map((section, index, sections) => {
        return (
            <LayerItem
            collapsed={typeof(section.collapsed) === 'boolean' ? section.collapsed : null}
            key={index}
            lastItem={typeof(section.lastItem) === 'boolean' ? section.lastItem : index === sections.length - 1}
            title={section.title}
            utils={utils}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {section.items.filter(item => {
                        return item.visible !== false;
                    }).map((item, index, items) => {
                        if(item.component === 'map') {
                            return getMapComponent(item, index);
                        }
                        return (
                            <div
                            key={index}
                            className={item.onClick ? `view-entry ${window.theme}` : null}
                            onClick={item.onClick?.bind(this, item)}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== items.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                display: 'flex',
                                flexDirection: 'row',
                                padding: '8px 12px 8px 12px',
                                width: '100%'
                            }}>
                                <span style={{
                                    ...Appearance.textStyles.key(),
                                    flexShrink: 1,
                                    ...item.style?.key
                                }}>{item.title}</span>
                                {getValue(item)}
                                {typeof(item.onClick) === 'function' && (
                                    <img
                                    src={'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 12,
                                        marginLeft: 8,
                                        objectFit: 'contain',
                                        width: 12
                                    }} />
                                )}
                            </div>
                        )
                    })}
                    {section.bottomContent}
                </div>
            </LayerItem>
        )
    })
}
export default FieldMapper;
